import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { userService } from "@/services/userService";
import mixpanel from "mixpanel-browser";

type Theme = "light" | "dark";

type ThemeContextValue = {
  theme: Theme;
  setTheme: (theme: Theme, syncToBackend?: boolean) => void;
  toggleTheme: () => void;
  resetToSystemTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getBrowserTheme(): Theme {
  if (typeof window === "undefined") return "light";
  const prefersDark =
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  return prefersDark ? "dark" : "light";
}

function getInitialTheme(): Theme {
  // Use browser/system preference as default
  // Theme from API will override this on login
  return getBrowserTheme();
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const debounceTimerRef = useRef<number | null>(null);
  const DEBOUNCE_DELAY = 500; // 500ms debounce

  const applyThemeClass = useCallback((nextTheme: Theme) => {
    const root = document.documentElement;
    if (nextTheme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, []);

  const setTheme = useCallback(
    (nextTheme: Theme, syncToBackend: boolean = true) => {
      setThemeState(nextTheme);
      applyThemeClass(nextTheme);

      // Debounce backend sync - only sync after user stops toggling
      if (syncToBackend && isAuthenticated) {
        // Clear existing timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Set new timer to sync the latest theme value
        debounceTimerRef.current = window.setTimeout(() => {
          userService.updateTheme(nextTheme);
          debounceTimerRef.current = null;
        }, DEBOUNCE_DELAY);
      }

      // Track theme change for analytics
      try {
        mixpanel.track("Theme Changed", { theme: nextTheme });
        // eslint-disable-next-line no-empty
      } catch {}
    },
    [applyThemeClass, isAuthenticated]
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // One-time cleanup: Remove old theme from localStorage
  useEffect(() => {
    try {
      localStorage.removeItem("theme");
      // eslint-disable-next-line no-empty
    } catch {}
  }, []);

  const toggleTheme = useCallback(() => {
    const next: Theme = theme === "dark" ? "light" : "dark";
    setTheme(next);
  }, [theme, setTheme]);

  const resetToSystemTheme = useCallback(() => {
    if (typeof window === "undefined") return;

    // Reset to system/browser preference
    const next = getBrowserTheme();
    setThemeState(next);
    applyThemeClass(next);

    // Sync to backend if authenticated
    if (isAuthenticated) {
      userService.updateTheme(next);
    }
  }, [applyThemeClass, isAuthenticated]);

  useEffect(() => {
    applyThemeClass(theme);
  }, [theme, applyThemeClass]);

  // Listen for browser theme changes (when user is not authenticated)
  useEffect(() => {
    // Only auto-update theme from system when not authenticated
    // When authenticated, theme comes from backend
    if (isAuthenticated) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      const newTheme: Theme = e.matches ? "dark" : "light";
      setThemeState(newTheme);
      applyThemeClass(newTheme);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [isAuthenticated, applyThemeClass]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggleTheme, resetToSystemTheme }),
    [theme, setTheme, toggleTheme, resetToSystemTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
